import { FormEvent } from "react"
import { useForm } from "@inertiajs/react"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { formatDateTime, formatMoney } from "@/lib/formatters"
import { cn } from "@/lib/utils"
import {
  TILL_PATHS,
  tTill,
  type CashMovementRow,
  type TillOption,
} from "@/features/staff/till"

/**
 * Cash that enters or leaves the drawer without being a sale: a supplier paid
 * in cash, a bank deposit, a float top-up, a correction.
 *
 * The form sits above the list rather than behind a button, for the same reason
 * the open form is the empty state — recording a movement is why the cashier
 * came to this section, and a movement recorded ten minutes late is a movement
 * recorded from memory.
 *
 * The form is deliberately not `preserveState`. Every submit re-renders it
 * fresh, which clears the amount: a filled amount field surviving a successful
 * write is how the same 500-peso supplier payment gets filed twice.
 *
 * Amounts are never signed here. `signedAmount` arrives from the server the way
 * the drawer sees it, so the direction is read, never re-derived from `kind`
 * and gotten backwards.
 */

type TillCashMovementsProps = {
  movements: CashMovementRow[]
  kindOptions: TillOption[]
  reasonOptions: TillOption[]
  timeZone: string
}

export function TillCashMovements({
  movements,
  kindOptions,
  reasonOptions,
  timeZone,
}: TillCashMovementsProps) {
  const { data, setData, post, processing } = useForm({
    kind: kindOptions[0]?.value ?? "",
    reason: reasonOptions[0]?.value ?? "",
    amount: "",
    note: "",
  })

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault()
    post(TILL_PATHS.movements, { preserveScroll: true })
  }

  return (
    <Card density="compact">
      <CardHeader className="border-b">
        <CardTitle>{tTill("movements_title")}</CardTitle>
        <CardDescription>{tTill("movements_description")}</CardDescription>
      </CardHeader>

      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-4" data-testid="cash-movement-form">
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="cash-movement-kind">{tTill("kind_label")}</Label>
              <Select value={data.kind} onValueChange={(value) => setData("kind", value)}>
                <SelectTrigger
                  id="cash-movement-kind"
                  className="w-full"
                  data-testid="cash-movement-kind"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {kindOptions.map((option) => (
                    <SelectItem
                      key={option.value}
                      value={option.value}
                      data-testid={`cash-movement-kind-option-${option.value}`}
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="cash-movement-reason">{tTill("reason_label")}</Label>
              <Select value={data.reason} onValueChange={(value) => setData("reason", value)}>
                <SelectTrigger
                  id="cash-movement-reason"
                  className="w-full"
                  data-testid="cash-movement-reason"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {reasonOptions.map((option) => (
                    <SelectItem
                      key={option.value}
                      value={option.value}
                      data-testid={`cash-movement-reason-option-${option.value}`}
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="cash-movement-amount">{tTill("amount_label")}</Label>
              <Input
                id="cash-movement-amount"
                data-testid="cash-movement-amount"
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                required
                autoComplete="off"
                className="tabular-nums"
                value={data.amount}
                onChange={(event) => setData("amount", event.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="cash-movement-note">{tTill("note_label")}</Label>
              <Input
                id="cash-movement-note"
                data-testid="cash-movement-note"
                autoComplete="off"
                maxLength={120}
                placeholder={tTill("note_placeholder")}
                value={data.note}
                onChange={(event) => setData("note", event.target.value)}
              />
            </div>
          </div>

          <Button type="submit" disabled={processing} data-testid="cash-movement-submit">
            {tTill("movement_submit")}
          </Button>
        </form>
      </CardContent>

      {movements.length === 0 ? (
        <CardContent className="border-t pt-4">
          <p className="text-sm text-muted-foreground" data-testid="cash-movements-empty">
            {tTill("movements_empty")}
          </p>
        </CardContent>
      ) : (
        <ul className="divide-y border-t" data-testid="cash-movements-list">
          {movements.map((movement) => (
            <li
              key={movement.id}
              className="flex items-center gap-3 px-4 py-2.5"
              data-testid={`cash-movement-${movement.id}`}
            >
              <span
                aria-hidden="true"
                className={cn(
                  "size-2 shrink-0 rounded-full",
                  movement.kind === "cash_in" ? "bg-success" : "bg-warning"
                )}
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">
                  {movement.reasonLabel}
                  {movement.note ? (
                    <span className="font-normal text-muted-foreground"> · {movement.note}</span>
                  ) : null}
                </p>
                <p className="truncate text-xs text-muted-foreground">
                  {movement.kindLabel}
                  {" · "}
                  {movement.actor ?? tTill("unknown_actor")}
                  {" · "}
                  {formatDateTime(movement.createdAt, { timeZone })}
                </p>
              </div>
              <span className="shrink-0 text-sm font-semibold tabular-nums text-foreground">
                {formatMoney(movement.signedAmount)}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  )
}
